import React, { useState } from 'react';
import { noop } from 'lodash';

import { useClaim } from '~/components/ClaimContainer';
import mixpanel from '~/components/CmsMain/mixpanel';
import Chip from '~/components/core/Atomic/Chip/Chip';
import { COMPLEXITY_MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import cn from '~/Utils/cn';

import ComplexityEditDialog from './ComplexityEditDialog';
import GenericClaimBarField from './GenericClaimBarField';

const COMPLEXITY_CONFIG = {
  default: { label: 'Not set', className: 'bg-slate-200 text-slate-800' },
  low: { label: 'Low', className: 'bg-green-100 text-green-700' },
  medium: { label: 'Medium', className: 'bg-orange-100 text-orange-700' },
  high: { label: 'High', className: 'bg-red-100 text-red-700' },
};

export const ComplexityTag: React.FC<{ complexity?: string | null; onClick: () => void }> = ({
  complexity,
  onClick = noop,
}) => {
  const key = complexity?.toLowerCase();
  const config =
    key && key in COMPLEXITY_CONFIG
      ? COMPLEXITY_CONFIG[key as keyof typeof COMPLEXITY_CONFIG]
      : COMPLEXITY_CONFIG.default;

  return (
    <Chip
      size="small"
      className={cn('ml-0 font-semibold', config.className)}
      label={config.label}
      onClick={onClick}
      clickable
    />
  );
};

const ClaimComplexity = ({ displayName = 'Complexity' }: { displayName?: string }) => {
  const { claim, onAsyncClaimUpdate } = useClaim({ claimRequired: true });
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const baseMixpanelProps = {
    claim_id: claim.id,
    line_of_business: claim.type,
    is_closed: claim.is_closed,
  };

  const handleOpenDialog = () => {
    mixpanel.track(COMPLEXITY_MIXPANEL_EVENTS.COMPLEXITY_DIALOG_OPENED, {
      ...baseMixpanelProps,
      current_complexity: claim.complexity ?? null,
      had_existing_reason: Boolean(claim.complexity_reason),
    });
    setIsDialogOpen(true);
  };

  return (
    <GenericClaimBarField title={displayName}>
      <ComplexityTag complexity={claim.complexity} onClick={handleOpenDialog} />
      {isDialogOpen && (
        <ComplexityEditDialog
          claimId={claim.id}
          currentComplexity={claim.complexity}
          currentReason={claim.complexity_reason}
          onClose={() => setIsDialogOpen(false)}
          onUpdate={onAsyncClaimUpdate}
          baseMixpanelProps={baseMixpanelProps}
        />
      )}
    </GenericClaimBarField>
  );
};

export default ClaimComplexity;
